import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import type { JobProfileSkill } from '~/shared/data-contract/skill';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';

import { useSkillProficiencyLevelName } from '../../../app/common/skills';
import { useSkillDetails } from '../../api/use-skill-details';

const Wrapper = styled.div(({ theme }) => ({
    '& h4': {
        ...theme.typography.body.S.medium,
        alignItems: 'flex-start',
        color: theme.colors.gray[900],
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing.s,
        justifyContent: 'flex-start',
        margin: 0,
    },
    color: theme.colors.gray[900],
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    justifyContent: 'flex-end',
    maxWidth: '27.75rem',
}));

export const ProficiencyLevelDropdownMenu: FunctionComponent<{
    readonly id: string;
    readonly activeLevel: number | null | undefined;
    readonly disabled: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
}> = ({ id, activeLevel, disabled, onChangeProficiencyLevel }) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();
    const { data: skillDetails } = useSkillDetails(id);

    if (is.undefined(skillDetails)) {
        return (
            <ScrollArea
                className="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
                orientation="vertical"
                type="hover"
                viewportClassName="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
            >
                {Array.from({ length: 10 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                    <DropdownMenuItem key={index}>
                        <span data-testid="loading-placeholder" />
                    </DropdownMenuItem>
                ))}
                <DropdownMenuItem />
            </ScrollArea>
        );
    }

    return (
        <ScrollArea
            className="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
            orientation="vertical"
            type="hover"
            viewportClassName="!h-auto max-h-[calc(var(--radix-dropdown-menu-content-available-height)-0.5rem)]"
        >
            {skillDetails?.proficiencyLevelDetails?.map((level) => (
                <DropdownMenuItem
                    key={level.proficiencyLevel}
                    disabled={disabled}
                    onClick={() => onChangeProficiencyLevel(id, level.proficiencyLevel)}
                >
                    <Wrapper>
                        <h4>
                            {getProficiencyLevelName({
                                level: level.proficiencyLevel,
                                skillType: skillDetails.skillType,
                            })}
                            <ProficiencyScale
                                active={activeLevel === level.proficiencyLevel}
                                level={level.proficiencyLevel}
                                scaleCount={5}
                            />
                        </h4>
                        <div>{level.description ?? ''}</div>
                    </Wrapper>
                </DropdownMenuItem>
            ))}
            <DropdownMenuItem />
        </ScrollArea>
    );
};
